import React, {
  createContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { node, bool, func } from 'prop-types';

import { useDataModel } from '@thd-nucleus/data-sources';

import { usePromoModel } from '../hooks/usePromoModel';
import {
  emptyPromoCart,
  getCartStoreSkusWithoutPromoItems,
  transformCartModelToPromoCart
} from '../utils/promo-cart-utils';
import { useCart } from '../hooks/useCart';
import { noop } from '../utils/promo-utils';
import { PromoCartProviderDataModel } from '../models/PromoCartProviderDataModel';

export const defaultCartContext = {
  promoCartValue: 0,
  promoCartQuantity: 0,
  promoCartItemsObj: {},
  firstListCartItems: [],
  firstListCartItemIds: [],
  firstListCartValue: 0,
  firstListCartQuantity: 0,
  secondListCartItems: [],
  secondListCartItemIds: [],
  secondListCartValue: 0,
  secondListCartQuantity: 0,
  bogoItemInCart: null,
  promoSuccessLevel: '',
  hasAnchorItemInCart: false,
  progressMade: 0,
  isPromoAchieved: false,
  currentPromoTier: 0,
  bogoSelectedProduct: null,
  setBogoSelectedProduct: noop,
  CartModel: {},
  addToCart: noop,
  updateCart: noop,
  freezeCart: noop,
  isCartLoading: false,
  setIsCartLoading: noop,
  selectedItemsModel: [],
  setSelectedItemsModel: noop
};

export const PromoCartContext = createContext(defaultCartContext);

export const PromoCartProvider = ({ children, hasUserInteracted, setHasUserInteracted }) => {
  const {
    allListProductsAndAnchor,
    anchorItem,
    displayableFirstListItemIds,
    displayableSecondListItemIds,
    isForwardsB1gy,
    isBackwardsBogo,
    isBmsm,
    isBogo,
    isForwardsBogo,
    isMsb,
    isDollarThresholdBogo,
    originalNvalue,
    rewardTiers,
    src1MinPurchaseAmount,
    src1EligibilityCriteria,
    tgt1EligibilityCriteria,
    subExperience,
  } = usePromoModel();
  const {
    CartModel,
    addToCart,
    updateCart,
    isCartLoading,
    setIsCartLoading,
    freezeCart
  } = useCart({ initializeCall: hasUserInteracted });

  const [promoCart, setPromoCart] = useState(emptyPromoCart);
  const [bogoSelectedProduct, setBogoSelectedProduct] = useState(null);
  const [selectedItemsModel, setSelectedItemsModel] = useState([]);

  // storeSkus of items that are in cart, and not included in allListProductsAndAnchor
  const filteredCartStoreSkus = getCartStoreSkusWithoutPromoItems(CartModel, allListProductsAndAnchor);

  const { data } = useDataModel('itemPromotionValidity', {
    variables: {
      navParam: originalNvalue,
      storeSkuIds: filteredCartStoreSkus,
    },
    ssr: false,
    skip: !filteredCartStoreSkus.length || !originalNvalue
  });

  useEffect(() => {
    if (isForwardsBogo || isBackwardsBogo) {
      // let buybox know there has been a bogo selection
      // clear the buybox bogo state when an item has been successfully added or unselected
      const bogoInfo = bogoSelectedProduct
        ? { bogoSelectedProduct, isFreeProduct: true }
        : null;

      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('buybox.bogoSelectedProduct', bogoInfo);
    }
  }, [
    bogoSelectedProduct,
    isBackwardsBogo,
    isBogo,
    isForwardsBogo,
    subExperience
  ]);

  useEffect(() => {
    if (promoCart?.bogoItemInCart) {
      setBogoSelectedProduct(null);
    }
  }, [promoCart]);

  // if add to cart to happens from another location, load our products/progress messaging
  useEffect(() => {
    if (CartModel && !hasUserInteracted) setHasUserInteracted(true);
  }, [CartModel, hasUserInteracted, setHasUserInteracted]);

  useEffect(() => {
    if (CartModel) {
      const newPromoCart = transformCartModelToPromoCart({
        rawCartModel: CartModel,
        selectedItemsModel,
        isForwardsB1gy,
        isBackwardsBogo,
        isForwardsBogo,
        isMsb,
        isBmsm,
        isDollarThresholdBogo,
        displayableFirstListItemIds,
        displayableSecondListItemIds,
        src1EligibilityCriteria,
        tgt1EligibilityCriteria,
        anchorItemId: anchorItem.itemId,
        bogoSelectedProduct,
        rewardTiers,
        src1MinPurchaseAmount,
        allListProductsAndAnchor,
        cartItemsPromotionValidity: data?.itemPromotionValidity || [],
      });
      if (newPromoCart) setPromoCart(newPromoCart);
    }
  }, [
    CartModel,
    selectedItemsModel,
    isForwardsB1gy,
    isBackwardsBogo,
    isForwardsBogo,
    isMsb,
    isBmsm,
    isDollarThresholdBogo,
    anchorItem.itemId,
    bogoSelectedProduct,
    rewardTiers,
    src1MinPurchaseAmount,
    src1EligibilityCriteria,
    tgt1EligibilityCriteria,
    allListProductsAndAnchor,
    data,
    displayableFirstListItemIds,
    displayableSecondListItemIds,
  ]);

  const contextValue = useMemo(() => ({
    // promoCart is only mutated as a sideEffect of updating rawCartModel
    promoCartValue: promoCart.promoCartValue,
    promoCartQuantity: promoCart.promoCartQuantity,
    promoCartItemsObj: promoCart.promoCartItemsObj,
    promoCartItems: Object.values(promoCart.promoCartItemsObj || {}),
    promoCartItemIds: Object.values(promoCart.promoCartItemsObj || {}).map(({ itemId }) => itemId),
    firstListCartItems: promoCart.firstListCartItems,
    firstListCartItemIds: promoCart.firstListCartItemIds,
    firstListCartValue: promoCart.firstListCartValue,
    firstListCartQuantity: promoCart.firstListCartQuantity,
    secondListCartItems: promoCart.secondListCartItems,
    secondListCartItemIds: promoCart.secondListCartItemIds,
    secondListCartValue: promoCart.secondListCartValue,
    secondListCartQuantity: promoCart.secondListCartQuantity,
    bogoItemInCart: promoCart.bogoItemInCart,
    promoSuccessLevel: promoCart.promoSuccessLevel,
    hasAnchorItemInCart: promoCart.hasAnchorItemInCart,
    // Percentage of promotion completed. For BMSM percentage of current tier completed
    progressMade: promoCart.progressMade,
    isPromoAchieved: promoCart.isPromoAchieved,
    currentPromoTier: promoCart.currentPromoTier,
    bogoSelectedProduct,
    setBogoSelectedProduct,
    secondListItemInCart: promoCart.secondListItemInCart,
    CartModel,
    addToCart,
    updateCart,
    freezeCart,
    isCartLoading,
    setIsCartLoading,
    selectedItemsModel,
    setSelectedItemsModel,
  }), [
    promoCart,
    bogoSelectedProduct,
    CartModel,
    addToCart,
    updateCart,
    freezeCart,
    isCartLoading,
    setIsCartLoading,
    selectedItemsModel,
    setSelectedItemsModel,
  ]);

  return (
    <PromoCartContext.Provider value={contextValue}>
      {children}
    </PromoCartContext.Provider>
  );
};

PromoCartProvider.propTypes = {
  children: node.isRequired,
  hasUserInteracted: bool.isRequired,
  setHasUserInteracted: func.isRequired,
};

PromoCartProvider.dataModel = PromoCartProviderDataModel;
